/* ==========================================================================
   NOURISH — Global behaviour
   Custom elements + shared helpers. No dependencies, no jQuery.
   Loaded with `defer` so it never blocks first paint.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */
  const Nourish = (window.Nourish = window.Nourish || {});

  Nourish.routes = window.routes || {};
  Nourish.strings = window.themeStrings || {};

  Nourish.formatMoney = function (cents) {
    const format = window.themeMoneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withCommas);
  };

  Nourish.debounce = function (fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  };

  /** Simple pub/sub so cart, drawer, and header badge stay in sync. */
  const subscribers = {};
  Nourish.subscribe = function (event, cb) {
    (subscribers[event] = subscribers[event] || []).push(cb);
    return () => {
      subscribers[event] = subscribers[event].filter((f) => f !== cb);
    };
  };
  Nourish.publish = function (event, data) {
    (subscribers[event] || []).forEach((cb) => cb(data));
  };

  Nourish.EVENTS = {
    cartUpdate: 'cart:update',
    cartError: 'cart:error',
    wishlistUpdate: 'wishlist:update'
  };

  /* ------------------------------------------------------------------------
     Focus management — used by every overlay so keyboard users are not
     dropped behind the scrim.
     ------------------------------------------------------------------------ */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  Nourish.trapFocus = function (container, firstElement) {
    const focusable = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!focusable.length) return () => {};

    const first = firstElement || focusable[0];
    const last = focusable[focusable.length - 1];

    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === focusable[0]) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        focusable[0].focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    requestAnimationFrame(() => first.focus());
    return () => container.removeEventListener('keydown', onKeydown);
  };

  let scrollLockCount = 0;
  let savedScroll = 0;
  Nourish.lockScroll = function () {
    if (scrollLockCount === 0) {
      savedScroll = window.scrollY;
      // Reserve the scrollbar width so locking does not shift the page
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = sbw + 'px';
      document.body.classList.add('scroll-lock');
    }
    scrollLockCount++;
  };
  Nourish.unlockScroll = function () {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.body.classList.remove('scroll-lock');
      document.body.style.paddingRight = '';
    }
  };

  /* ------------------------------------------------------------------------
     Toast
     ------------------------------------------------------------------------ */
  let toastTimer;
  Nourish.toast = function (message, type) {
    let el = document.querySelector('.toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.className = 'toast' + (type === 'error' ? ' toast--error' : '');
    el.textContent = message;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 4000);
  };

  /* ------------------------------------------------------------------------
     Section Rendering API helper
     ------------------------------------------------------------------------ */
  Nourish.fetchSection = function (sectionId, url) {
    const target = url || window.location.href;
    const sep = target.indexOf('?') > -1 ? '&' : '?';
    return fetch(`${target}${sep}section_id=${sectionId}`)
      .then((r) => r.text())
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  };

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  /* ------------------------------------------------------------------------
     Button ripple
     ------------------------------------------------------------------------ */
  if (document.body.dataset.ripple === 'true') {
    document.addEventListener('pointerdown', function (e) {
      const btn = e.target.closest('.btn');
      if (!btn || btn.disabled) return;
      const rect = btn.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const span = document.createElement('span');
      span.className = 'btn__ripple';
      span.style.width = span.style.height = size + 'px';
      span.style.left = e.clientX - rect.left - size / 2 + 'px';
      span.style.top = e.clientY - rect.top - size / 2 + 'px';
      btn.appendChild(span);
      setTimeout(() => span.remove(), 640);
    });
  }

  /* ------------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------------ */
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initReveal() {
    const items = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!items.length) return;

    if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    items.forEach((el) => observer.observe(el));
  }

  document.addEventListener('DOMContentLoaded', initReveal);
  document.addEventListener('shopify:section:load', initReveal);

  /* ------------------------------------------------------------------------
     <details-disclosure> — accessible dropdown wrapper for <details>
     ------------------------------------------------------------------------ */
  class DetailsDisclosure extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.querySelector('summary');
      this.content = this.summary && this.summary.nextElementSibling;
      this.details.addEventListener('toggle', this.onToggle.bind(this));
      document.addEventListener('click', this.onOutside.bind(this));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.details.open) {
          this.details.open = false;
          this.summary.focus();
        }
      });
    }
    onToggle() {
      if (this.details.open) this.setAttribute('open', '');
      else this.removeAttribute('open');
    }
    onOutside(e) {
      if (this.details.open && !this.contains(e.target)) this.details.open = false;
    }
  }
  customElements.define('details-disclosure', DetailsDisclosure);

  /* ------------------------------------------------------------------------
     <accordion-item> — grid-template-rows animation, no height maths
     ------------------------------------------------------------------------ */
  class AccordionItem extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.accordion__trigger');
      this.panel = this.querySelector('.accordion__panel');
      if (!this.trigger || !this.panel) return;
      this.trigger.addEventListener('click', () => this.toggle());
    }
    toggle() {
      const open = this.trigger.getAttribute('aria-expanded') === 'true';
      // Single-open groups close their siblings
      if (!open && this.closest('[data-accordion-single]')) {
        this.closest('[data-accordion-single]')
          .querySelectorAll('accordion-item')
          .forEach((item) => {
            if (item !== this) item.close();
          });
      }
      this.trigger.setAttribute('aria-expanded', String(!open));
      this.panel.setAttribute('aria-hidden', String(open));
    }
    close() {
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
    }
  }
  customElements.define('accordion-item', AccordionItem);

  /* ------------------------------------------------------------------------
     <tab-group>
     ------------------------------------------------------------------------ */
  class TabGroup extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => this.onKeydown(e, i));
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        this.panels[i].hidden = !active;
      });
    }
    onKeydown(e, i) {
      const map = { ArrowRight: 1, ArrowLeft: -1 };
      if (!(e.key in map)) return;
      e.preventDefault();
      const next = (i + map[e.key] + this.tabs.length) % this.tabs.length;
      this.select(next);
      this.tabs[next].focus();
    }
  }
  customElements.define('tab-group', TabGroup);

  /* ------------------------------------------------------------------------
     <carousel-slider> — scroll-snap driven, arrows + dots + a11y
     ------------------------------------------------------------------------ */
  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.slider');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.dots = Array.from(this.querySelectorAll('.slider-dots__dot'));

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollBy(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollBy(1));
      this.dots.forEach((dot, i) =>
        dot.addEventListener('click', () => this.goTo(i))
      );

      this.track.addEventListener('scroll', Nourish.debounce(() => this.update(), 80), {
        passive: true
      });
      this.update();

      // Keep arrow state correct when the container resizes
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(Nourish.debounce(() => this.update(), 120));
        this.ro.observe(this.track);
      }
    }
    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
    }
    get step() {
      const s = this.slides[0];
      if (!s) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return s.getBoundingClientRect().width + gap;
    }
    scrollBy(dir) {
      this.track.scrollBy({ left: this.step * dir, behavior: 'smooth' });
    }
    goTo(index) {
      this.track.scrollTo({ left: this.step * index, behavior: 'smooth' });
    }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;

      if (this.dots.length) {
        const index = Math.round(scrollLeft / this.step);
        this.dots.forEach((dot, i) =>
          dot.setAttribute('aria-current', String(i === index))
        );
      }
    }
  }
  customElements.define('carousel-slider', CarouselSlider);

  /* ------------------------------------------------------------------------
     <countdown-timer> — real end date only, never a rolling fake clock
     ------------------------------------------------------------------------ */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      const end = this.dataset.endDate;
      if (!end) return this.hideHost();
      this.end = new Date(end.replace(' ', 'T')).getTime();
      if (isNaN(this.end)) return this.hideHost();

      this.units = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]')
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    hideHost() {
      const host = this.closest('[data-countdown-host]') || this;
      host.hidden = true;
    }
    tick() {
      const diff = this.end - Date.now();
      if (diff <= 0) {
        clearInterval(this.timer);
        if (this.dataset.onEnd === 'hide') this.hideHost();
        this.setAll(0, 0, 0, 0);
        return;
      }
      const s = Math.floor(diff / 1000);
      this.setAll(
        Math.floor(s / 86400),
        Math.floor((s % 86400) / 3600),
        Math.floor((s % 3600) / 60),
        s % 60
      );
    }
    setAll(d, h, m, sec) {
      const pad = (n) => String(n).padStart(2, '0');
      if (this.units.days) this.units.days.textContent = pad(d);
      if (this.units.hours) this.units.hours.textContent = pad(h);
      if (this.units.minutes) this.units.minutes.textContent = pad(m);
      if (this.units.seconds) this.units.seconds.textContent = pad(sec);
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ------------------------------------------------------------------------
     <announcement-rotator>
     ------------------------------------------------------------------------ */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.announcement__slide'));
      if (this.slides.length < 2 || prefersReducedMotion.matches) return;
      this.index = 0;
      const speed = parseInt(this.dataset.speed, 10) || 5000;
      this.timer = setInterval(() => this.next(), speed);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    next() {
      this.slides[this.index].hidden = true;
      this.index = (this.index + 1) % this.slides.length;
      this.slides[this.index].hidden = false;
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ------------------------------------------------------------------------
     <drawer-element> — cart drawer, mobile nav, filter drawer
     ------------------------------------------------------------------------ */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.drawer') || this;
      this.overlay = this.querySelector('.overlay');
      this.releaseFocus = null;

      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-open');
      this.setAttribute('open', '');
      this.drawer.setAttribute('aria-hidden', 'false');
      Nourish.lockScroll();
      this.releaseFocus = Nourish.trapFocus(this.drawer);
    }
    close() {
      if (!this.hasAttribute('open')) return;
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-open');
      this.removeAttribute('open');
      this.drawer.setAttribute('aria-hidden', 'true');
      Nourish.unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      if (this.opener && this.opener.focus) this.opener.focus();
    }
  }
  customElements.define('drawer-element', DrawerElement);

  // Any [data-drawer-open="id"] button opens the matching drawer
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    drawer.open(trigger);
  });

  /* ------------------------------------------------------------------------
     <modal-element>
     ------------------------------------------------------------------------ */
  class ModalElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.modal__panel');
      this.querySelectorAll('[data-modal-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      this.addEventListener('click', (e) => {
        if (e.target === this) this.close();
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      Nourish.lockScroll();
      this.releaseFocus = Nourish.trapFocus(this);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      Nourish.unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      if (this.opener && this.opener.focus) this.opener.focus();
      this.dispatchEvent(new CustomEvent('modal:close'));
    }
  }
  customElements.define('modal-element', ModalElement);

  /* ------------------------------------------------------------------------
     <quantity-input>
     ------------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = btn.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min, 10) || 0;
          const next = Math.max(min, (parseInt(this.input.value, 10) || 0) + step);
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------------
     <sticky-header> — hides on scroll down, returns on scroll up
     ------------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.setHeightVar();
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => this.setHeightVar()).observe(this);
      }
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
    setHeightVar() {
      document.documentElement.style.setProperty(
        '--header-height',
        this.offsetHeight + 'px'
      );
    }
    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('header-wrapper--scrolled', y > 8);
      if (this.hideOnScroll) {
        const goingDown = y > this.lastY && y > 220;
        // Never hide while a menu inside the header is open
        const menuOpen = this.querySelector('.nav__item.is-open, details[open]');
        this.classList.toggle('header-wrapper--hidden', goingDown && !menuOpen);
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ------------------------------------------------------------------------
     <mega-nav> — hover on pointer devices, click and keyboard everywhere
     ------------------------------------------------------------------------ */
  class MegaNav extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.nav__item--has-children'));
      this.items.forEach((item) => {
        const link = item.querySelector('.nav__link');
        const panel = item.querySelector('.mega');
        if (!link || !panel) return;

        link.addEventListener('click', (e) => {
          if (window.matchMedia('(hover: hover)').matches && link.href) return;
          e.preventDefault();
          this.toggle(item);
        });

        item.addEventListener('mouseenter', () => this.open(item));
        item.addEventListener('mouseleave', () => this.close(item));
        item.addEventListener('focusin', () => this.open(item));
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) this.close(item);
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.items.forEach((i) => this.close(i));
      });
    }
    open(item) {
      this.items.forEach((i) => i !== item && this.close(i));
      item.classList.add('is-open');
      const link = item.querySelector('.nav__link');
      if (link) link.setAttribute('aria-expanded', 'true');
    }
    close(item) {
      item.classList.remove('is-open');
      const link = item.querySelector('.nav__link');
      if (link) link.setAttribute('aria-expanded', 'false');
    }
    toggle(item) {
      item.classList.contains('is-open') ? this.close(item) : this.open(item);
    }
  }
  customElements.define('mega-nav', MegaNav);

  /* ------------------------------------------------------------------------
     <compare-slider> — before/after
     ------------------------------------------------------------------------ */
  class CompareSlider extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('.compare__range');
      if (!this.range) return;
      this.range.addEventListener('input', () => this.set(this.range.value));
      this.set(this.range.value);
    }
    set(value) {
      this.style.setProperty('--pos', value + '%');
      this.range.setAttribute('aria-valuenow', value);
    }
  }
  customElements.define('compare-slider', CompareSlider);

  /* ------------------------------------------------------------------------
     <video-facade> — swaps the poster for the real player on click
     ------------------------------------------------------------------------ */
  class VideoFacade extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.video-facade');
      if (!this.button) return;
      this.button.addEventListener('click', () => this.load(), { once: true });
    }
    load() {
      const template = this.querySelector('template');
      if (!template) return;
      const fragment = template.content.cloneNode(true);
      this.button.remove();
      this.appendChild(fragment);
      const media = this.querySelector('iframe, video');
      if (media && media.tagName === 'VIDEO') media.play();
    }
  }
  customElements.define('video-facade', VideoFacade);

  /* ------------------------------------------------------------------------
     <localstorage-wishlist>
     Stored on the visitor's device. Nothing is sent anywhere.
     ------------------------------------------------------------------------ */
  const WISHLIST_KEY = 'nourish:wishlist';

  Nourish.wishlist = {
    read() {
      try {
        const raw = localStorage.getItem(WISHLIST_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        return [];
      }
    },
    write(list) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      } catch (e) {
        /* storage full or blocked — fail quietly, the UI still works this session */
      }
      Nourish.publish(Nourish.EVENTS.wishlistUpdate, list);
    },
    has(handle) {
      return this.read().indexOf(handle) > -1;
    },
    toggle(handle) {
      const list = this.read();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1);
      else list.push(handle);
      this.write(list);
      return i === -1;
    }
  };

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle;
      this.button = this.querySelector('button');
      if (!this.button || !this.handle) return;
      this.sync();
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const added = Nourish.wishlist.toggle(this.handle);
        this.sync();
        Nourish.toast(
          added
            ? Nourish.strings.wishlistAdded || 'Saved'
            : Nourish.strings.wishlistRemoved || 'Removed'
        );
      });
      this.unsubscribe = Nourish.subscribe(Nourish.EVENTS.wishlistUpdate, () => this.sync());
    }
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }
    sync() {
      const saved = Nourish.wishlist.has(this.handle);
      this.button.setAttribute('aria-pressed', String(saved));
      const label = saved
        ? Nourish.strings.wishlistSaved || 'Saved'
        : Nourish.strings.wishlistSave || 'Save for later';
      this.button.setAttribute('aria-label', label);
      const text = this.querySelector('[data-wishlist-text]');
      if (text) text.textContent = label;
    }
  }
  customElements.define('wishlist-button', WishlistButton);

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.render();
      this.unsubscribe = Nourish.subscribe(Nourish.EVENTS.wishlistUpdate, () => this.render());
    }
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }
    render() {
      const count = Nourish.wishlist.read().length;
      this.textContent = count;
      this.classList.toggle('header__bubble--empty', count === 0);
    }
  }
  customElements.define('wishlist-count', WishlistCount);

  /* Wishlist page — renders saved products from the Section Rendering API */
  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.render();
      this.unsubscribe = Nourish.subscribe(Nourish.EVENTS.wishlistUpdate, () => this.render());
    }
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }
    async render() {
      const handles = Nourish.wishlist.read();
      const empty = this.querySelector('[data-wishlist-empty]');
      const grid = this.querySelector('[data-wishlist-items]');
      if (!grid) return;

      if (!handles.length) {
        grid.innerHTML = '';
        if (empty) empty.hidden = false;
        return;
      }
      if (empty) empty.hidden = true;
      grid.setAttribute('aria-busy', 'true');

      const cards = await Promise.all(
        handles.map((handle) =>
          fetch(`${Nourish.routes.root || '/'}products/${handle}?section_id=wishlist-card`)
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => '')
        )
      );

      grid.innerHTML = cards
        .map((html) => {
          if (!html) return '';
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('[data-card-wrapper]');
          return inner ? inner.outerHTML : '';
        })
        .join('');
      grid.setAttribute('aria-busy', 'false');
      initReveal();
    }
  }
  customElements.define('wishlist-grid', WishlistGrid);

  /* ------------------------------------------------------------------------
     Recently viewed — local only
     ------------------------------------------------------------------------ */
  const RV_KEY = 'nourish:recently-viewed';

  Nourish.recentlyViewed = {
    read() {
      try {
        return JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      } catch (e) {
        return [];
      }
    },
    add(handle, limit) {
      if (!handle) return;
      let list = this.read().filter((h) => h !== handle);
      list.unshift(handle);
      list = list.slice(0, limit || 8);
      try {
        localStorage.setItem(RV_KEY, JSON.stringify(list));
      } catch (e) {}
    }
  };

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const current = this.dataset.currentProduct;
      const handles = Nourish.recentlyViewed.read().filter((h) => h !== current);
      const limit = parseInt(this.dataset.limit, 10) || 4;
      if (handles.length < 2) {
        this.hidden = true;
        return;
      }
      this.render(handles.slice(0, limit));
    }
    async render(handles) {
      const grid = this.querySelector('[data-recently-viewed-grid]');
      if (!grid) return;
      const cards = await Promise.all(
        handles.map((handle) =>
          fetch(`${Nourish.routes.root || '/'}products/${handle}?section_id=wishlist-card`)
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => '')
        )
      );
      const html = cards
        .map((h) => {
          if (!h) return '';
          const doc = new DOMParser().parseFromString(h, 'text/html');
          const inner = doc.querySelector('[data-card-wrapper]');
          return inner ? inner.outerHTML : '';
        })
        .join('');
      if (!html.trim()) {
        this.hidden = true;
        return;
      }
      grid.innerHTML = html;
      this.hidden = false;
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  // Record the current product on the product page
  if (document.body.dataset.productHandle) {
    Nourish.recentlyViewed.add(
      document.body.dataset.productHandle,
      parseInt(document.body.dataset.recentlyViewedLimit, 10) || 8
    );
  }
})();
